/**
 * Pure formatters for the FK Breadcrumb chip text + hover tooltip.
 *
 * Extracted from `FkBreadcrumbChip` so the label format is testable in
 * isolation. Row chips include the schema-qualified table name so users
 * can identify which row sits where in the breadcrumb tree without
 * relying on adjacent chips:
 *
 *   single-PK   →  `dbo.CUSTOMER#1111`
 *   composite   →  `Production.BillOfMaterials#(7|A)`
 *
 * A node's `displayLabel` (if set) overrides the synthesized label —
 * used by callers that prefer a human-friendly name (e.g. the row's
 * `name` column).
 */

import type { NavigationNode } from '../../../core/types/workspaceTypes';

/** Schema-prefixed table name (`schema.table`), or bare table for empty schemas. */
function qualifiedTableName(node: { schema: string; table: string }): string {
  return node.schema ? `${node.schema}.${node.table}` : node.table;
}

/** PK suffix: `#1111` for single-column, `#(7|A)` for composite. */
function pkSuffix(pk: ReadonlyArray<{ column: string; value: unknown }>): string {
  const parts = pk.map((p) => String(p.value));
  if (parts.length === 1) return `#${parts[0]}`;
  return `#(${parts.join('|')})`;
}

export function chipLabel(node: NavigationNode): string {
  if (node.displayLabel) return node.displayLabel;
  if (node.kind === 'table') return qualifiedTableName(node);
  return `${qualifiedTableName(node)}${pkSuffix(node.pk)}`;
}

export function chipTooltip(node: NavigationNode): string {
  const tableName = qualifiedTableName(node);
  if (node.kind === 'table') return tableName;
  const pkPairs = node.pk.map((p) => `${p.column}=${String(p.value)}`).join(', ');
  return `${tableName} — ${pkPairs}`;
}
